/**
 * useVersionCheck.tsx — app-wide version-drift polling (the automatic
 * version checker).
 *
 * ONE poller for the whole app (the useConnectivity pattern): every 60s and
 * on tab-focus it probes
 *   - the same-origin /version.json     — what the DEPLOY serves now
 *     (404 = an older deploy without the manifest → checks stay null/silent)
 *   - GET {backendOrigin}/api/version   — what the BACKEND runs now (only
 *     when a backend is connected)
 * and derives:
 *   - frontendUpdate: the baked sourceMd5 ≠ the served one → the deploy
 *     moved on; offer a reload.
 *   - stackUpdate: the served release digests ≠ the live ones (registry-
 *     pulled stacks only — locally built images have no registry digest to
 *     compare) → the Update modal / in-app updater.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { useAuth } from '../auth/AuthContext';
import { httpOriginOf } from '../connect/httpOrigin';
import {
  bakedManifest,
  frontendDrift,
  parseManifest,
  stackDrift,
  type LiveVersionInfo,
  type VersionManifest,
} from './manifest';

const POLL_MS = 60_000;

export interface VersionCheckState {
  /** True until the first round landed (both probes settled or failed). */
  checking: boolean;
  /** The same-origin served manifest (null = absent/unreadable). */
  served: VersionManifest | null;
  /** The live backend version info (null = no backend / unreachable). */
  live: LiveVersionInfo | null;
  /** Baked ≠ served frontend hash — a reload picks up the new deploy.
   *  null = unknown (no served manifest — an older deploy). */
  frontendUpdate: boolean | null;
  /** Served release digests ≠ live digests. null = unknown (no backend, no
   *  served digests, or a locally-built stack). */
  stackUpdate: boolean | null;
  /** Force an immediate re-check (e.g. after an update completed). */
  check: () => void;
}

const VersionContext = createContext<VersionCheckState | null>(null);

export function VersionProvider({ children }: { children: ReactNode }): JSX.Element {
  const auth = useAuth();
  const backendOrigin = auth.wsUrl ? httpOriginOf(auth.wsUrl, '') : null;
  const [served, setServed] = useState<VersionManifest | null>(null);
  const [live, setLive] = useState<LiveVersionInfo | null>(null);
  const [firstRound, setFirstRound] = useState(true);
  const [nonce, setNonce] = useState(0);

  const check = useCallback((): void => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    const probe = (): void => {
      // Same-origin served manifest — cache-busted (nginx leaves it
      // uncached, but dev servers may not).
      fetch('/version.json', { cache: 'no-store' })
        .then(async (res) => {
          if (!res.ok) return null;
          return parseManifest(await res.text());
        })
        .catch(() => null)
        .then((m) => {
          if (!cancelled) setServed(m);
        });
      // Live backend digests — only meaningful when connected.
      if (backendOrigin) {
        fetch(`${backendOrigin}/api/version`, {
          headers: auth.token ? { Authorization: `Bearer ${auth.token}` } : {},
        })
          .then(async (res) => {
            if (!res.ok) return null;
            return (await res.json()) as LiveVersionInfo;
          })
          .catch(() => null)
          .then((info) => {
            if (!cancelled) setLive(info);
          });
      } else if (!cancelled) {
        setLive(null);
      }
    };
    probe();
    const timer = window.setInterval(probe, POLL_MS);
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') probe();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [backendOrigin, auth.token, nonce]);

  // First-round bookkeeping: both async probes above settle async; a short
  // timer flips `checking` off so the UI doesn't flash on the first paint.
  useEffect(() => {
    if (!firstRound) return;
    const t = window.setTimeout(() => setFirstRound(false), 1500);
    return () => window.clearTimeout(t);
  }, [firstRound]);

  const value = useMemo<VersionCheckState>(() => {
    const frontendUpdate = frontendDrift(bakedManifest, served);
    const stackUpdate = stackDrift(served, live);
    return {
      checking: firstRound,
      served,
      live,
      frontendUpdate,
      stackUpdate,
      check,
    };
  }, [served, live, firstRound, check]);

  return <VersionContext.Provider value={value}>{children}</VersionContext.Provider>;
}

export function useVersionCheck(): VersionCheckState {
  const ctx = useContext(VersionContext);
  if (!ctx) throw new Error('useVersionCheck must be used within <VersionProvider>.');
  return ctx;
}
